import React, {  } from 'react';
import get from 'lodash/get';
import * as StyledComponents from '../UnifyAdministration/Notification/StyledComponents';
import { PrimaryButton, DatePicker } from 'cdk-radial';
import { useUnifyAdminStore } from '../UnifyAdministration/AdminStore';
import '../UnifyAdministration/Notification/Blocks/notification.css';
import { MultiSelectDropdownMenu } from '../UnifyAdministration/Notification/Elements/MultiSelectDropdownMenu';

import { createLinkModel } from './Store/modelTransformation';

import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { useCreateUserLink, useUpdateUserLink, useUserProfile } from '../../hooks/queries';
import { StyledUserCardForm, StyledWarning, StyledInput, StyledTextArea } from './StyledComponents';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';

type Positions = {
  top: string;
  bottom: string;
  left: string;
  right: string;
};

type ActionButtonsContainer = {
  children: React.ReactNode;
  position?: string;
  positionProps?: Partial<Positions>;
  background?: string;
  zIndex?: string
};

const ActionButtonContainer = ({
  children,
  zIndex,
  position,
  positionProps,
  background,
}: ActionButtonsContainer): JSX.Element => {
  return (
    <StyledComponents.StyledActionButtonContainer
      positionProps={positionProps}
      position={position}
      background={background}
      zIndex={zIndex}
    >
      {children}
    </StyledComponents.StyledActionButtonContainer>
  );
};

 const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9._~!$&'()*+,;=:@/-]*)?(\?[a-zA-Z0-9._~!$&'()*+,;=:@/?-]*)?(#[a-zA-Z0-9._~!$&'()*+,;=:@/?-]*)?$/;

export const StyledWrapperDatePicker = styled.div<{
  maxWidth: string;
  dayPickerLeft: string;
}>`
  max-width: ${(props) => props.maxWidth || '150px'};
  .DayPicker {
    left: ${(props) => props.dayPickerLeft || 'unset'};
    bottom: 0px;
    top: -320px;
    margin-bottom: 55px;
    z-index:1000;
  }
`;

export const LinkSheetDetails = (): JSX.Element => {
  const { darkMode } = useTheme();
  const departmentOptions = useUnifyAdminStore((state) => state.departmentList);
  const { t } = useTranslation();
  const { data: userProfile } = useUserProfile();
  const { trackEvent } = useAnalytics();

  const sideSheetData = useUnifyAdminStore(
    (state) => state.linkSideSheetData
  );
  const showStartDateValidation = useUnifyAdminStore((state) => state.showStartDateValidation);
  const setShowStartDateValidation = useUnifyAdminStore((state) => state.setShowStartDateValidation);
  const showEndDateValidation = useUnifyAdminStore((state) => state.showEndDateValidation);
  const setShowEndDateValidation = useUnifyAdminStore((state) => state.setShowEndDateValidation);
  const setIsLinkValidation = useUnifyAdminStore((state) => state.setIsLinkValidationState);

  const enterpriseId = userProfile?.userProfile?.enterpriseId;
  const loginId = userProfile?.userProfile?.email;

  const onDepartmentSelect = (departments: any) => {
    updateLinkSideSheetData({
      fieldName: 'targetDepartments',
      value: departments,
    });
  };

  const onStartDateChange = (startDate: any) => {

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    setShowStartDateValidation(false);
    if (startDate < today) {
      setShowStartDateValidation(true);
    } else {
      updateLinkSideSheetData({
        fieldName: 'startDate',
        value: startDate,
      });
    }
  };
  const onLinkNameChange = (event: any) => {
    updateLinkSideSheetData({ fieldName: 'name', value: event.target.value });
  };
  const onLinkDescChange = (event: any) => {
    updateLinkSideSheetData({ fieldName: 'description', value: event.target.value });
  };
  const onLinkUrlChange = (event: any) => {
    updateLinkSideSheetData({ fieldName: 'url', value: event.target.value });
  };
  const onEndDateChange = (endDate: any) => {
    if (!endDate) {
      setShowEndDateValidation(false);
      updateLinkSideSheetData({ fieldName: 'endDate', value: null });
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    setShowEndDateValidation(false);
    if (endDate < today) {
      setShowEndDateValidation(true);
    } else {
      updateLinkSideSheetData({ fieldName: 'endDate', value: endDate });
    }
  };

  const { mutate: createMutation } = useCreateUserLink({
    onSuccess: () => {
      setLinkShowToastState(t('linksAdmin.toastCreateSuccess'), 'Success');
      closeSideSheet()
    },
    onError: (msg?: string) => {

      const toastMsg = msg ? msg : t('linksAdmin.toastCreateError');
      setLinkShowToastState(toastMsg, 'Error');
      closeSideSheet()
    }
  });

  const { mutate: updateMutation } = useUpdateUserLink({
    onSuccess: () => {
      setLinkShowToastState(t('linksAdmin.toastUpdateSuccess'), 'Success');
      closeSideSheet()
    },
    onError: () => {
      setLinkShowToastState(t('linksAdmin.toastUpdateError'), 'Error');
      closeSideSheet()
    }
  });

  const handleSave = () => {
    if (!handleValidation()) return;

    const iconText = sideSheetData.name?.split(' ').reduce((acc, word) => { return acc + word.substring(0, 1) }, '').substring(0, 2);

    sideSheetData.iconText = iconText;
    sideSheetData.enterpriseId = enterpriseId;
    sideSheetData.loginId = loginId;

    if (sideSheetData.appId) {
      updateMutation(createLinkModel(sideSheetData));
    } else {
      trackEvent(AMPLITUDE_EVENTS.CREATE_LINK, {
                  'Link Name': sideSheetData.name
              });
      createMutation(createLinkModel(sideSheetData));
    }
  };

  const handleValidation = () => {
    if (!sideSheetData.name) {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidName'), 'Error');
      return false;
    }
    if (!sideSheetData.description) {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidDescription'), 'Error');
      return false;
    }
    
    if (!sideSheetData.url && sideSheetData.url.trim() === "") {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidUrl'), 'Error');
      return false;
    } else if(!urlPattern.test(sideSheetData.url.trim())){
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidUrlFormat'), 'Error');
      return false;
    }

    if (!sideSheetData.startDate) {
      setIsLinkValidation(true);
      setShowStartDateValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidStartDate'), 'Error');
      return false;
    }

    if (showEndDateValidation) {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastInvalidEndDate'), 'Error');
      return false;
    }

    const startDateV = moment(
      sideSheetData.startDate, "DD/MM/YYYY").startOf("day");

    const todaysDate = moment().startOf("day");

    const hasEndDate = !!sideSheetData.endDate;
    const endDateV = hasEndDate
      ? moment(sideSheetData.endDate, "DD/MM/YYYY").startOf("day")
      : null;

    if (!sideSheetData.appId) {
      if (startDateV.isBefore(todaysDate)) {
        setIsLinkValidation(true);
        setLinkShowToastState(t('linksAdmin.toastStartDateFuture'), 'Error');
        return false;
      }
    }

    if (hasEndDate && startDateV.isAfter(endDateV!)) {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastEndDateAfterStart'), 'Error');
      return false;
    }

    if (hasEndDate && endDateV!.isBefore(startDateV)) {
      setIsLinkValidation(true);
      setLinkShowToastState(t('linksAdmin.toastEndDateAfterStart'), 'Error');
      return false;
    }

    return true;
  };

  const setLinkSideSheetCollapse = useUnifyAdminStore(
    (state) => state.setLinkSideSheetCollapse
  );
  const resetLinkSideSheetState = useUnifyAdminStore(
    (state) => state.resetLinkSideSheetState
  );
  const archiveSwitch = useUnifyAdminStore((state) => state.linkArchiveSwitch);
  const updateLinkSideSheetData = useUnifyAdminStore(
    (state) => state.updateLinkSideSheetData
  );
  const setLinkShowToastState = useUnifyAdminStore(
    (state) => state.setLinkShowToastState
  );

  const closeSideSheet = () => {
    setLinkSideSheetCollapse(true);
    resetLinkSideSheetState();
  };

  const handleCancel = () => {
    closeSideSheet();
  };

  return (
    <StyledUserCardForm>

      <StyledInput
        dataTestId="link-name-app"
        errorMessage={t('userCardForm.nameError')}
        hideBorder="none"
        iconPosition="trailing"
        id="link-name-app"
        inputDivider="left"
        label={t('userCardForm.name')}
        name="link-name-app"
        onChange={(e: any) => onLinkNameChange(e)}
        placeholder={t('userCardForm.nameHolder')}
        size="standard"
        maxLength="300"
        isRequired
        value={get(sideSheetData, 'name', '')}
        isDisabled={archiveSwitch} />

      <StyledTextArea
        dataTestId="usr-card-des"
        errorMessage={t('userCardForm.desError')}
        hideBorder="none"
        iconPosition="trailing"
        id="usr-card-des"
        inputDivider="left"
        label={t('userCardForm.description')}
        name="usr-card-des"
        onChange={(e: any) => onLinkDescChange(e)}
        placeholder={t('userCardForm.desHolder')}
        size="standard"
        maxLength="300"
        rows="3"
        isRequired
        value={get(sideSheetData, 'description', '')}
        isDisabled={archiveSwitch} />

      <StyledInput
        dataTestId="usr-card-url"
        errorMessage={t('userCardForm.urlError')}
        hideBorder="none"
        iconPosition="trailing"
        id="usr-card-url"
        inputDivider="left"
        label={t('userCardForm.url')}
        name="usr-card-url"
        onChange={(e: any) => onLinkUrlChange(e)}
        placeholder="https://link.com"
        size="standard"
        maxLength="200"
        hasError={""}
        enableCustomValidation
        isRequired
        value={get(sideSheetData, 'url', '')}
        isDisabled={archiveSwitch} />
      <MultiSelectDropdownMenu
        options={departmentOptions}
        name="targetDepartments"
        onSelect={onDepartmentSelect}
        value={get(sideSheetData, 'targetDepartments', [])}
        label={t('userCardForm.targetDepartments')}
        placeholder={t('userCardForm.selectDepartments')}
        isDisabled={archiveSwitch}
      ></MultiSelectDropdownMenu>
      <>
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ width: '180px' }}>
            <StyledWrapperDatePicker dayPickerLeft="0px" maxWidth="180px">
              <DatePicker
                dataTestId="start-date-picker"
                date={get(sideSheetData, 'startDate', null)}
                dateFormat="MM/DD/YYYY"
                dateInputProps={{
                  enableCustomValidation: true,
                  errorMessage: t('linksAdmin.invalidStartDate'),
                  hasError: showStartDateValidation,
                  isReadOnly: false,
                  isRequired: true,
                  isDisabled: archiveSwitch,
                  size: 'standard',
                  onError: () => { },
                }}
                id="start-date-picker"
                label={t('userCardForm.startDate')}
                maximumYear={2100}
                minimumYear={1900}
                name="start-date-picker"
                numberOfMonths={1}
                onDateChange={onStartDateChange}
                shortcutChipsPosition="none"
                highlightToday
              />
            </StyledWrapperDatePicker>
          </div>

          <div style={{ width: '180px' }}>
            <StyledWrapperDatePicker dayPickerLeft="-132px" maxWidth="180px">
              <DatePicker
                className="endDatePicker"
                dataTestId="end-date-picker"
                date={get(sideSheetData, 'endDate', null)}
                dateFormat="MM/DD/YYYY"
                dateInputProps={{
                  enableCustomValidation: true,
                  errorMessage: t('linksAdmin.invalidEndDate'),
                  hasError: showEndDateValidation,
                  isReadOnly: false,
                  isRequired: false,
                  isDisabled: archiveSwitch,
                  size: 'standard',
                  onError: () => { },
                }}
                id="end-date-picker"
                label={t('userCardForm.endDate')}
                maximumYear={2100}
                minimumYear={1900}
                name="end-date-picker"
                numberOfMonths={1}
                onDateChange={onEndDateChange}
                shortcutChipsPosition="none"

              />
            </StyledWrapperDatePicker>
          </div>
        </div>
      </>
      <StyledWarning>{ }</StyledWarning>

      <ActionButtonContainer
        position="sticky"
        zIndex="999"
        positionProps={{ bottom: '0px' }}
        background={
          darkMode
            ? 'linear-gradient(black, transparent)'
            : 'linear-gradient(white, transparent)'
        }
      >
        <StyledComponents.StyledTextButton
          dataTestId="text-button"
          iconColor="primary"
          onClick={handleCancel}
          text={t('common.cancel')}
        />
        {!archiveSwitch && (
        <PrimaryButton
          dataTestId="save-side-sheet-button"
          iconColor="primary"
          onClick={handleSave}
          text={get(sideSheetData, 'appId') ? t('linksAdmin.update') : t('linksAdmin.save')}
        />
        )}
      </ActionButtonContainer>
    </StyledUserCardForm>
  );
};